import React, { createContext, useContext, useReducer, ReactNode } from 'react';

interface WorkDescription {
  id?: string;
  description: string;
  category: string;
  timeframe: string;
}

interface PriceRange {
  min: number;
  max: number;
  confidence: 'low' | 'medium' | 'high';
}

interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  zipCode: string;
  preferredContact: 'email' | 'phone' | 'text';
  numberOfQuotes: string;
  previousVendors: string;
  consentToContact: boolean;
}

interface ClarifyingQuestion {
  id: string;
  question: string;
  type: 'text' | 'select' | 'boolean';
  options?: string[];
  answer?: string | boolean;
}

interface QuoteState {
  workDescription: WorkDescription | null;
  priceRange: PriceRange | null;
  contactInfo: ContactInfo | null;
  clarifyingQuestions: ClarifyingQuestion[];
}

type QuoteAction = 
  | { type: 'SET_WORK_DESCRIPTION'; payload: WorkDescription }
  | { type: 'SET_PRICE_RANGE'; payload: PriceRange }
  | { type: 'SET_CONTACT_INFO'; payload: ContactInfo }
  | { type: 'SET_CLARIFYING_QUESTIONS'; payload: ClarifyingQuestion[] }
  | { type: 'UPDATE_QUESTION_ANSWER'; payload: { id: string; answer: string | boolean } }
  | { type: 'RESET' };

const initialState: QuoteState = {
  workDescription: null,
  priceRange: null,
  contactInfo: null,
  clarifyingQuestions: [],
};

const quoteReducer = (state: QuoteState, action: QuoteAction): QuoteState => {
  switch (action.type) {
    case 'SET_WORK_DESCRIPTION':
      return {
        ...state,
        workDescription: action.payload,
      };
    case 'SET_PRICE_RANGE':
      return {
        ...state,
        priceRange: action.payload,
      };
    case 'SET_CONTACT_INFO':
      return {
        ...state,
        contactInfo: action.payload,
      };
    case 'SET_CLARIFYING_QUESTIONS':
      return {
        ...state,
        clarifyingQuestions: action.payload,
      };
    case 'UPDATE_QUESTION_ANSWER':
      return {
        ...state,
        clarifyingQuestions: state.clarifyingQuestions.map(q =>
          q.id === action.payload.id
            ? { ...q, answer: action.payload.answer }
            : q
        ),
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

const QuoteContext = createContext<{
  state: QuoteState;
  dispatch: React.Dispatch<QuoteAction>;
}>({
  state: initialState,
  dispatch: () => null,
});

export const QuoteProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(quoteReducer, initialState);
  
  return (
    <QuoteContext.Provider value={{ state, dispatch }}>
      {children}
    </QuoteContext.Provider>
  );
};

export const useQuote = () => {
  const context = useContext(QuoteContext);
  if (!context) {
    throw new Error('useQuote must be used within a QuoteProvider');
  }
  return context;
};